import { CreateImageRequest, ImageUpload } from '../types';
import { LitElement, css, html } from 'lit';
import { arrayBufferToBase64, debounce, isDefined } from '../helpers/utils';
import { blobToDataUrl, rotateImage } from '../helpers/files';
import { customElement, property, query, state } from 'lit/decorators';

import { ImageSizingGuide } from '../helpers/constants';
import { TextField } from '@material/mwc-textfield';
import { detectMobileViewport } from '../helpers/device';
import { getMainPage } from '../helpers/page-name';
import piexif from 'piexifjs';
import topicData from '../helpers/state/topic-data';
import { uploadImage } from '../helpers/api/image-api';

@customElement('image-upload-component')
export class ImageUploadComponent extends LitElement {

    amplitude_callsite: string = 'image-upload-component';

    @query('#fileInput')
    fileInput?: HTMLInputElement;

    @query('#imageUrlInput')
    imageUrlInput?: TextField;

    @property({ type: Boolean })
    border: boolean = false;

    @property({ type: Boolean })
    allowMultiple: boolean = false;

    @property({ type: String })
    imageUrl: string = '';

    @property({ type: String })
    imageUrls: string[] = [];

    @property({ type: Boolean })
    urlOnly: boolean = false;

    @property({ type: Boolean })
    autoUpload: boolean = false;

    @property({ type: Boolean })
    disabled: boolean = false;

    @state()
    activeTab: string = 'upload';

    @state()
    autoUploading: boolean = false;

    @state()
    imageToUpload?: ImageUpload;

    @state()
    imagesToUpload?: ImageUpload[];

    @state()
    newUrl: string = '';

    async updated(changedProperties: any) {
        // Listen for 'point' changes so we can fill in form.
        changedProperties.forEach(async (oldValue: any, propName: any) => {
            if (propName === 'imageUrl') {
                this.newUrl = this.imageUrl;
            }
        });
    }

    private preventDefault(event: any) {
        event.preventDefault();
    }

    private async processImage(file: File): Promise<ImageUpload> {
        if (file.size > 15728640) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'The image you uploaded is too large. Please upload a smaller image.'
            );
        }

        try {
            const base64file = await blobToDataUrl(file);
            const exifData = piexif.load(base64file);
            const orientation = exifData['0th'][274];

            // [mhauhgey ]Removing temporarily because it is rotating images incorrectly and I dont remember why it was added.
            // we can re-add it later if needed.
            // // If EXIF orientation is not available, use canvas to infer orientation
            // if (!isDefined(orientation)) {
            //     console.warn('EXIF orientation not found, inferring from image dimensions');
            //     const img = new Image();
            //     img.src = base64file;
            //     await new Promise<void>((resolve) => {
            //         img.onload = () => {
            //             if (img.width > img.height) {
            //                 orientation = 1; // Landscape
            //             } else {
            //                 orientation = 6; // Portrait
            //             }
            //             resolve();
            //         };
            //     });
            //     console.warn('Inferred orientation:', orientation);
            // }

            return { file, orientation };
        } catch {
            return { file, orientation: undefined };
        }
    }

    async handleRotate(index?: number) {
        if (this.imageToUpload && index === undefined) {
            const rotatedFile = await rotateImage(this.imageToUpload);
            this.imageToUpload = rotatedFile;
            const css = `rotate(${rotatedFile.rotate_value ?? 0}deg)`;

            this.requestUpdate();

            this.dispatchEvent(new CustomEvent('on-change-image', {
                detail: {
                    imageToUpload: this.imageToUpload,
                    rotatePreviewCSS: css
                },
                bubbles: true,
                composed: true
            }));
        }

        if (this.imagesToUpload && index !== undefined) {
            const rotatedFile = rotateImage(this.imagesToUpload[index]);
            this.imagesToUpload[index] = rotatedFile;

            this.requestUpdate();

            this.dispatchEvent(new CustomEvent('on-change-image', {
                detail: {
                    imagesToUpload: this.imagesToUpload
                },
                bubbles: true,
                composed: true
            }));
        }
    }

    // TODO: Refactor to auto upload image when added. Will simplify a lot of code.
    private async onDrop(event: any) {
        event.preventDefault();
        const file = event.dataTransfer.items[0].getAsFile();
        const image = await this.processImage(file);

        if (this.allowMultiple) {
            this.imagesToUpload = this.imagesToUpload ? this.imagesToUpload.concat(image) : [image];
        } else {
            if (this.fileInput) this.fileInput.value = '';
            this.imageToUpload = image;
        }

        this.dispatchEvent(new CustomEvent('on-change-image', {
            detail: {
                imageToUpload: this.imageToUpload,
                imagesToUpload: this.imagesToUpload
            },
            bubbles: true,
            composed: true
        }));

        await this.requestUpdate();
    }

    private async onFileInputChange(event: any) {
        event.preventDefault();

        const files = Array.from(event.target.files as FileList);
        const images: ImageUpload[] = [];
        for (const file of files) {
            const image = await this.processImage(file);
            images.push(image);
        }

        if (this.allowMultiple) {
            this.imagesToUpload = this.imagesToUpload ? this.imagesToUpload.concat(images) : images;
        } else {
            this.imageToUpload = images[0];
        }

        this.dispatchEvent(new CustomEvent('on-change-image', {
            detail: {
                imageToUpload: this.imageToUpload,
                imagesToUpload: this.imagesToUpload
            },
            bubbles: true,
            composed: true
        }));

        this.requestUpdate();
    }

    private async onAutoUpload(event: any) {
        event.preventDefault();

        this.autoUploading = true;

        const file = event.target.files[0];
        const image = await this.processImage(file);

        this.imageToUpload = image;

        if (!isDefined(file)) {
            this.autoUploading = false;
            return;
        }

        const imageRequest: CreateImageRequest = {
          name: file.name,
          data: arrayBufferToBase64(await image.file.arrayBuffer()),
        };

        const image_url = await uploadImage(
          this.amplitude_callsite,
          imageRequest,
          image.orientation
        );

        this.dispatchEvent(new CustomEvent('retreived-autoupload-url', {
            detail: {
                imageUrl: image_url,
            },
            bubbles: true,
            composed: true
        }));

        this.autoUploading = false;
    }

    // can call from parent component to upload images
    async uploadPendingImages(): Promise<string[]> {
        if (this.imagesToUpload) {
            const imageRequests: CreateImageRequest[] = [];
            for (const image of this.imagesToUpload) {
                const imageRequest: CreateImageRequest = {
                    name: image.file.name,
                    data: arrayBufferToBase64(await image.file.arrayBuffer()),
                    new_orientation: image.orientation
                };
                imageRequests.push(imageRequest);
            }

            const image_urls = await Promise.all(imageRequests.map(async (imageRequest) => {
                return await uploadImage(
                    this.amplitude_callsite,
                    imageRequest,
                    imageRequest.new_orientation
                );
            }));

            return image_urls;
        }

        return [];
    }

    private onAddFilesClick() {
        this.fileInput?.click();
    }

    private async setActiveTab(tab: string) {
        this.activeTab = tab;

        if (tab === 'url') {
           await this.handleRemoveImage();
        }
    }

    private async handleRemoveImage(file?: ImageUpload) {
        if (!this.allowMultiple) {
            if (this.fileInput) this.fileInput.value = '';
            this.imageToUpload = undefined;
        } else {
            if (file) {
                this.imagesToUpload = this.imagesToUpload?.filter((image) => image.file !== file.file);
            } else {
                this.imagesToUpload = undefined;
            }
        }

        this.dispatchEvent(new CustomEvent('on-change-image', {
            detail: {
                imageToUpload: this.imageToUpload,
                imagesToUpload: this.imagesToUpload
            },
            bubbles: true,
            composed: true
        }));

        await this.requestUpdate();
    }

    private getObjectURL(image: ImageUpload): string {
        return URL.createObjectURL(image.file);
    }

    private sizingGuideClick() {
        const link = ImageSizingGuide;
        window.open(link, '_blank');
    }

    private readonly imageUrlChanged = debounce(this.onChangeImageURL, 500);

    onChangeImageURL() {
        this.dispatchEvent(new CustomEvent('changed-image-url', {
            detail: {
                imageUrl: this.imageUrlInput?.value ?? '',
            }
        }));

        if (this.autoUpload) {
            this.dispatchEvent(new CustomEvent('retreived-autoupload-url', {
                detail: {
                    imageUrl: this.imageUrlInput?.value ?? '',
                },
                bubbles: true,
                composed: true
            }));
        }
    }

    render() {
        return html`

            <div class="image-upload-component__container ${this.border ? 'show-border' : ''}">
                ${this.allowMultiple || this.urlOnly ? '' : html`
                    <div class="image-upload-component__tabs">
                        <div class="image-upload-component__tab ${this.activeTab === 'upload' ? 'active' : ''}" @click="${async () => await this.setActiveTab('upload')}">Upload</div>
                        <div class="image-upload-component__tab ${this.activeTab === 'url' ? 'active' : ''}" @click="${async () => await this.setActiveTab('url')}">Use Image URL</div>
                    </div>
                `}

                ${this.activeTab === 'upload' ? html`
                ${this.autoUploading ? html`
                    <loading-element loadingString="Uploading Image..."></loading-element>
                ` : html`
                    <div class="image-upload-component__photo-upload-wrapper"
                        @drop="${this.onDrop}"
                        @dragover="${this.preventDefault}"
                        @dragenter="${this.preventDefault}"
                    >
                        <div class="image-upload-component__photo-area">
                            <form-button class="image-upload-component__photo-btn" size="sm"
                                borderStyle="round"
                                @click="${this.disabled ? '' : this.onAddFilesClick}"
                                ?disabled="${this.disabled}"
                            >
                                <mwc-icon style="margin-right: 0.25rem">upload</mwc-icon>
                                ${this.allowMultiple ? html`<translated-span>Add Images</translated-span>` : html`<translated-span>Add Image</translated-span>`}
                            </form-button>
                            ${detectMobileViewport() ? '' : html`
                                <div class="image-upload-component__photo-subtext"><translated-span>or drag it in</translated-span></div>
                                `}
                        </div>
                        <div class="image-upload-component__images">
                            ${this.imageToUpload ? html`
                                    <div class="image-container">
                                        <div class="buttonContainer">
                                                <mwc-icon-button
                                                    class="imageButton"
                                                    @click="${async () => await this.handleRotate()}"
                                                    raised
                                                    icon="autorenew"
                                                ></mwc-icon-button>
                                            <mwc-icon-button
                                                class="imageButton"
                                                @click="${async () => await this.handleRemoveImage()}"
                                                raised
                                                icon="close"
                                            ></mwc-icon-button>
                                        </div>
                                        <img
                                            src="${this.getObjectURL(this.imageToUpload)}"
                                            class="gallery-item"
                                            style="transform:${isDefined(this.imageToUpload.rotate_value) ? `rotate(${this.imageToUpload.rotate_value}deg)` : ''}"
                                        />
                                    </div>
                                `
                                : ''
                            }
                            ${this.imagesToUpload?.map((image, index) => html`
                                <div class="image-container">
                                    <div class="buttonContainer">
                                        <mwc-icon-button
                                            class="imageButton"
                                            @click="${async () => await this.handleRotate(index)}"
                                            raised
                                            icon="autorenew"
                                        ></mwc-icon-button>
                                        <mwc-icon-button
                                            class="imageButton"
                                            @click="${async () => await this.handleRemoveImage(image)}"
                                            raised
                                            icon="close"
                                        ></mwc-icon-button>
                                    </div>
                                    <img
                                        src="${this.getObjectURL(image)}"
                                        class="gallery-item"
                                        style="transform:${isDefined(image.rotate_value) ? `rotate(${image.rotate_value}deg)` : ''}"
                                    />
                                </div>
                            `)}
                        </div>
                        <input
                            type="file"
                            id="fileInput"
                            accept="image/*"
                            ?multiple="${this.allowMultiple}"
                            hidden
                            @change="${this.autoUpload ? this.onAutoUpload : this.onFileInputChange}"/>
                    </div>
                `}
                ` : this.allowMultiple ? '' : html`
                    <div class="image-upload-component__url-wrapper">
                        <mwc-textfield
                            id="imageUrlInput"
                            class="image-upload-component__url"
                            outlined
                            value=${this.imageUrl}
                            placeholder="paste image url here."
                            @input=${this.imageUrlChanged}
                            ?disabled=${this.disabled}
                        ></mwc-textfield>
                    </div>
                `}

            </div>
            ${topicData.isAddPointer ? html`
                <span class="image-upload-component__sizing-guide" @click="${this.sizingGuideClick}" style="display: block; text-align: center; margin-top: 0; width: 100%;margin-bottom: 10px;">
                    <a href="${ImageSizingGuide}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; color: var(--main-primary-color); cursor: pointer; font-size: 10px;">
                        View Our Image Sizing Guide
                    </a>
                </span>
            ` : ''}
        `;
    }

    static get styles() {
        return css`
            .image-upload-component__container {
                width: 100%;
                max-width: 566px;
                height: auto;
                display: flex;
                flex-direction: column;
                padding: 10px 0;
                box-sizing: border-box;
            }

            .image-upload-component__container.show-border {
                border: 1px dashed var(--theme-input-border-color);
                border-radius: 5px;
            }

            .image-upload-component__tabs {
                display: flex;
                justify-content: flex-start;
            }

            .image-upload-component__tab {
                padding-bottom: 0.25rem;
                line-height: 1.5;
                color: var(--theme-lighter-text-color);
                cursor: pointer;
                margin-right: 8px;
                flex: 1;
                text-align: center;
            }

            .image-upload-component__tab.active {
                font-weight: 700;
                border-bottom: 2px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .image-upload-component__photo-upload-wrapper {
                width: 100%;
                display: flex;
                flex-wrap: wrap;
                border: 1px dashed var(--theme-input-border-color);
                border-radius: 12px;
                padding: 12px 20px;
                gap: 5px;
                box-sizing: border-box;
            }

            .image-upload-component__photo-area {
                width: 100%;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-wrap: wrap;
                gap: 5px;
            }

            .image-upload-component__photo-btn {
                --btn-background-color: var(--main-primary-color);
            }

            .image-upload-component__photo {
                height: 100%;
            }

            .image-upload-component__photo-subtext {
                font-size: 16px;
                line-height: 1.5;
                text-align: center;
                color: var(--mdc-text-field-ink-color, rgba(0, 0, 0, 0.87));
            }

            .image-upload-component__images {
                width: 100%;
            }

            .image-container {
                position: relative;
                display: inline-block;
                gap: 5px;
            }

            .gallery-item {
                height: 4rem;
                display: inline-block;
                max-width: 100%;
                border-radius: 10px;
                border: 1px solid var(--main-primary-color);
            }

            .gallery-item img {
                height: 100%;
                border-radius: 10px;
            }

            .imageButton {
                border-radius: 50%;
                background-color: var(--main-moon-color);
                --mdc-icon-button-size: 24px;
            }

            .buttonContainer {
                position: absolute;
                top: -3px;
                right: -3px;
                z-index: 1;
            }

            .image-upload-component__url-wrapper {
                width: 100%;
                padding-bottom: 24px;
                box-sizing: border-box;
            }

            .image-upload-component__url {
                width: 100%;
                height: 44px;
            }
        `;
    }
}
